import React from 'react';
import styles from './DifficultyAddons.module.css';
import { TIME_LIMIT_VALUES, getTimeLimitFromIndex, getIndexFromTimeLimit, formatTimeLimit } from '../../constants/timeLimit';

interface DifficultyAddonsProps {
  isBlurry: boolean;
  onIsBlurryChange: (value: boolean) => void;
  blurAmount: number;
  onBlurAmountChange: (value: number) => void;
  hasTimeLimit: boolean;
  onHasTimeLimitChange: (value: boolean) => void;
  timeLimitMs: number;
  onTimeLimitMsChange: (value: number) => void;
  isCropped: boolean;
  onIsCroppedChange: (value: boolean) => void;
  cropPercentage: number;
  onCropPercentageChange: (value: number) => void;
  isFlipped: boolean;
  onIsFlippedChange: (value: boolean) => void;
  isInverted: boolean;
  onIsInvertedChange: (value: boolean) => void;
  isMosaic: boolean;
  onIsMosaicChange: (value: boolean) => void;
  isNoColor: boolean;
  onIsNoColorChange: (value: boolean) => void;
}

const DifficultyAddons: React.FC<DifficultyAddonsProps> = ({
  isBlurry,
  onIsBlurryChange,
  blurAmount,
  onBlurAmountChange,
  hasTimeLimit,
  onHasTimeLimitChange,
  timeLimitMs,
  onTimeLimitMsChange,
  isCropped,
  onIsCroppedChange,
  cropPercentage,
  onCropPercentageChange,
  isFlipped,
  onIsFlippedChange,
  isInverted,
  onIsInvertedChange,
  isMosaic,
  onIsMosaicChange,
  isNoColor,
  onIsNoColorChange,
}) => {
  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Difficulty Add-ons</h2>
      <div className={styles.option}>
        <label>
          <input
            type="checkbox"
            checked={isBlurry}
            onChange={(e) => onIsBlurryChange(e.target.checked)}
          />
          Blurry Images
        </label>
      </div>
      {isBlurry && (
        <div className={styles.inputGroup}>
          <label htmlFor="blurAmount">Blur Amount (px):</label>
          <input
            id="blurAmount"
            type="range"
            min={5}
            max={30}
            step={1}
            value={blurAmount}
            onChange={(e) => onBlurAmountChange(parseInt(e.target.value || '10', 10))}
            className={styles.slider}
          />
          <div className={styles.sliderValue}>{blurAmount}px</div>
        </div>
      )}
      <div className={styles.option}>
        <label>
          <input
            type="checkbox"
            checked={hasTimeLimit}
            onChange={(e) => onHasTimeLimitChange(e.target.checked)}
          />
          Time Limit
        </label>
      </div>
      {hasTimeLimit && (
        <div className={styles.inputGroup}>
          <label htmlFor="timeLimitMs">Time Limit:</label>
          <input
            id="timeLimitMs"
            type="range"
            min={0}
            max={TIME_LIMIT_VALUES.length - 1}
            step={1}
            value={getIndexFromTimeLimit(timeLimitMs)}
            onChange={(e) => {
              const index = parseInt(e.target.value, 10);
              onTimeLimitMsChange(getTimeLimitFromIndex(index));
            }}
            className={styles.slider}
          />
          <div className={styles.rangeValues}>
            <span>5.0s (easier)</span>
            <span>0.1s (harder)</span>
          </div>
          <div className={styles.sliderValue}>{formatTimeLimit(timeLimitMs)}</div>
        </div>
      )}
      <div className={styles.option}>
        <label>
          <input
            type="checkbox"
            checked={isCropped}
            onChange={(e) => onIsCroppedChange(e.target.checked)}
          />
          Cropped (show {100 - cropPercentage}% of image)
        </label>
      </div>
      {isCropped && (
        <div className={styles.inputGroup}>
          <label htmlFor="cropPercentage">Crop Amount (% to remove from edges):</label>
          <input
            id="cropPercentage"
            type="range"
            min={10}
            max={80}
            step={5}
            value={cropPercentage}
            onChange={(e) => onCropPercentageChange(parseInt(e.target.value || '30', 10))}
            className={styles.slider}
          />
          <div className={styles.rangeValues}>
            <span>Show 90%</span>
            <span>Show 20%</span>
          </div>
          <div className={styles.sliderValue}>Remove {cropPercentage}% (Show {100 - cropPercentage}%)</div>
        </div>
      )}
      <div className={styles.option}>
        <label>
          <input
            type="checkbox"
            checked={isFlipped}
            onChange={(e) => onIsFlippedChange(e.target.checked)}
          />
          Flip Upside Down
        </label>
      </div>
      <div className={styles.option}>
        <label>
          <input
            type="checkbox"
            checked={isInverted}
            onChange={(e) => onIsInvertedChange(e.target.checked)}
          />
          Invert Colors
        </label>
      </div>
      <div className={styles.option}>
        <label>
          <input
            type="checkbox"
            checked={isMosaic}
            onChange={(e) => onIsMosaicChange(e.target.checked)}
          />
          Mosaic Scramble
        </label>
      </div>
      <div className={styles.option}>
        <label>
          <input
            type="checkbox"
            checked={isNoColor}
            onChange={(e) => onIsNoColorChange(e.target.checked)}
          />
          No Color (grayscale)
        </label>
      </div>
    </div>
  );
};

export default DifficultyAddons;
